import { Fragment, type ReactNode, useCallback, useMemo } from 'react';
import { FormField, Header, HStack, VStack } from '@direct-frontend/components';
import { type BaseValidationError, useField, useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { GeoSuggestLocalityInput } from '@/entities/geoSuggest';
import { useLoadingOrder } from '@/entities/order';
import { trackGoal } from '@/entities/trackGoal';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- Проблема в текущем файле, тк OrderForm должен быть в widgets
import { LoginButton } from '@/features/LoginButton';
import type { DeliveryChunk } from '@/shared/api/order';
import { OrderDeliveryMethods } from '@/shared/api/order';
import { Footer } from '@/shared/components/Footer';
import { buildLocation } from '@/shared/lib/buildLocation';
import { getRequestContext } from '@/shared/lib/requestContext';
import { ChunkIntervalInput } from './components/ChunkIntervalInput/ChunkIntervalInput';
import { CourierAddressSelect } from './components/CourierAddressSelect';
import { DeliveryOptionsInput } from './components/DeliveryOptionsInput';
import { OrderClientInput } from './components/OrderClientInput';
import { OrderDeliveryChunk } from './components/OrderDeliveryChunk';
import { PickupPointSelect } from './components/PickupPointSelect';
import { orderForm } from './model/forms/orderForm';
import { SelfPickUpAddressInput } from './components';
import { ORDER_FORM_ID } from './constants';
import { useIsInvalidOzonUser, useOrderFormUpdateReaction } from './lib';
import styles from './OrderForm.module.css';

const ERRORS_MAPPING: Record<string, string> = {
    REQUIRED_DELIVERY_METHOD: 'Необходимо выбрать способ доставки',
    REQUIRED_DELIVERY_ADDRESS: 'Необходимо выбрать адрес доставки',
    UNSUPPORTED_ADDRESS: 'Не можем доставить по этому адресу',
    REQUIRED_DELIVERY_FLOOR: 'Необходимо указать этаж',
    REQUIRED_CLIENT: 'Необходимо заполнить все данные получателя',
    INVALID_NAME_FORMAT: 'Неверный формат имени или фамилии',
    INVALID_PHONE_FORMAT: 'Неверный формат телефона',
    INVALID_EMAIL_FORMAT: 'Неверный формат email',
    REQUIRED_PAYMENT_METHOD: 'Необходимо выбрать способ оплаты',
    REQUIRED_PHONE_VERIFICATION: 'Необходимо верифицировать номер телефона',
} as const;

export const buildErrorMessage = (errors: BaseValidationError[]): string | undefined => {
    const [validationError] = errors;

    if (validationError?.code) {
        return ERRORS_MAPPING[validationError.code] ?? validationError.code;
    }

    return undefined;
};

interface OrderFormProps {
    deliveryChunks: DeliveryChunk[];
    openRegistrationOzon: VoidFunction;
    bottomAddon?: ReactNode;
}

export const OrderForm = (props: OrderFormProps) => {
    const { deliveryChunks, openRegistrationOzon, bottomAddon } = props;

    const { fields } = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const { account } = getRequestContext();

    useOrderFormUpdateReaction();

    const fieldClient = useField(fields.client);
    const fieldLocality = useField(fields.locality);
    const fieldDeliveryMethod = useField(fields.deliveryMethod);
    const fieldAddressCourier = useField(fields.addressCourier);
    const fieldAddressPickup = useField(fields.addressPickup);
    const fieldAddressSelfPickUp = useField(fields.addressSelfPickUp);

    const isCourierDeliveryMethod = fieldDeliveryMethod.value === OrderDeliveryMethods.COURIER;
    const isPickupPointDeliveryMethod = fieldDeliveryMethod.value === OrderDeliveryMethods.PICKUP_POINT;
    const isSelfPickUpPointDeliveryMethod = fieldDeliveryMethod.value === OrderDeliveryMethods.SELF_PICK_UP;

    const availableDeliveryChunks = useMemo(
        () => deliveryChunks.filter((chunk) => chunk.is_available),
        [deliveryChunks]
    );

    const getChunkTitle = useCallback(
        (index: number, isAvailable: boolean) => {
            if (!isAvailable) {
                return 'Эти товары вернём в корзину';
            }

            if (availableDeliveryChunks.length > 1) {
                return `Посылка ${index + 1}`;
            }

            return 'Состав заказа';
        },
        [availableDeliveryChunks.length]
    );

    const { isOrderSideUpdating } = useLoadingOrder();

    const isInvalidOzonUser = useIsInvalidOzonUser({ type: 'form' });

    return (
        <VStack justifyContent="space-between" className={styles.container}>
            <VStack gap="28">
                {!account.loggedIn && <LoginButton />}
                <VStack gap="8">
                    <HStack gap="16" justifyContent="space-between" alignItems="center">
                        <Header level="h3">Оформление заказа</Header>
                        <GeoSuggestLocalityInput
                            {...fieldLocality.getProps()}
                            className={styles.localityInput}
                            onChange={fields.locality.setValue}
                            aria-label="Город"
                            size="m"
                        />
                    </HStack>
                    <VStack gap="12">
                        {fieldLocality.value && (
                            <Fragment>
                                <FormField
                                    size="s"
                                    errorMessage={buildErrorMessage(fieldDeliveryMethod.state.errors)}
                                    {...fieldDeliveryMethod.getScrollProps()}
                                >
                                    {() => (
                                        <DeliveryOptionsInput
                                            {...fieldDeliveryMethod.getProps()}
                                            onChange={(value) => {
                                                trackGoal('delivery_type_changed', {
                                                    delivery_type: value,
                                                });
                                                fieldDeliveryMethod.getProps().onChange(value);
                                            }}
                                            aria-label="Способ получения"
                                            options={context.deliveryOptions.orderDeliveryOptions}
                                            isLoading={context.deliveryOptions.isLoading || isOrderSideUpdating}
                                            validationState={
                                                buildErrorMessage(fieldDeliveryMethod.state.errors)
                                                    ? 'invalid'
                                                    : 'valid'
                                            }
                                        />
                                    )}
                                </FormField>

                                <CourierAddressSelect
                                    isCourierDeliveryMethod={isCourierDeliveryMethod}
                                    context={context}
                                    fieldAddressCourierValue={fieldAddressCourier.value}
                                    fieldLocalityValue={fieldLocality.value}
                                    setValue={fields.addressCourier.setValue}
                                    errors={fieldAddressCourier.state.errors}
                                    scrollProps={fieldAddressCourier.getScrollProps()}
                                    openRegistrationOzon={openRegistrationOzon}
                                />

                                <PickupPointSelect
                                    context={context}
                                    isPickupPointDeliveryMethod={isPickupPointDeliveryMethod}
                                    fieldAddressPickupValue={fieldAddressPickup.value}
                                    fieldAddressCourierValue={fieldAddressCourier.value}
                                    fieldLocalityValue={fieldLocality.value}
                                    setValue={fields.addressPickup.setValue}
                                    errors={fieldAddressPickup.state.errors}
                                    scrollProps={fieldAddressPickup.getScrollProps()}
                                    openRegistrationOzon={openRegistrationOzon}
                                />

                                {isSelfPickUpPointDeliveryMethod && (
                                    <FormField
                                        size="s"
                                        errorMessage={buildErrorMessage(fieldAddressSelfPickUp.state.errors)}
                                        {...fieldAddressSelfPickUp.getScrollProps()}
                                    >
                                        {(fieldProps) => (
                                            <SelfPickUpAddressInput
                                                {...fieldProps}
                                                placeholder="Магазин продавца"
                                                order={context.order}
                                                storeSlug={context.storeSlug}
                                                value={fieldAddressSelfPickUp.value}
                                                onChange={fields.addressSelfPickUp.setValue}
                                                defaultLocation={
                                                    buildLocation(fieldAddressSelfPickUp.value) ||
                                                    buildLocation(fieldAddressCourier.value) ||
                                                    fieldLocality.value
                                                }
                                                size="m"
                                                validationState={
                                                    buildErrorMessage(fieldAddressSelfPickUp.state.errors)
                                                        ? 'invalid'
                                                        : 'valid'
                                                }
                                            />
                                        )}
                                    </FormField>
                                )}
                            </Fragment>
                        )}
                        {deliveryChunks.length === 1 ? (
                            <ChunkIntervalInput
                                chunkId={deliveryChunks[0].id}
                                openRegistrationOzon={openRegistrationOzon}
                            />
                        ) : null}
                        <FormField
                            size="s"
                            errorMessage={buildErrorMessage(fieldClient.state.errors)}
                            {...fieldClient.getScrollProps()}
                        >
                            {() => (
                                <OrderClientInput
                                    onChange={fields.client.setValue}
                                    openRegistrationOzon={openRegistrationOzon}
                                    isInvalidOzonUser={isInvalidOzonUser}
                                    orderClientContext={context.order.client}
                                    validationState={buildErrorMessage(fieldClient.state.errors) ? 'invalid' : 'valid'}
                                    value={fieldClient.value}
                                />
                            )}
                        </FormField>
                    </VStack>
                </VStack>
                <VStack gap="64">
                    <VStack gap="12">
                        <VStack gap="36">
                            {deliveryChunks.map((chunk, index) => (
                                <OrderDeliveryChunk
                                    key={index}
                                    title={getChunkTitle(index, chunk.is_available)}
                                    caption={chunk.is_available ? '' : 'Сможете заказать их отдельно'}
                                    chunkId={chunk.id}
                                    items={chunk.items}
                                    shouldShowChunkIntervalInput={deliveryChunks.length > 1 && chunk.is_available}
                                    isDisabled={!chunk.is_available}
                                    openRegistrationOzon={openRegistrationOzon}
                                />
                            ))}
                        </VStack>

                        {bottomAddon}
                    </VStack>

                    <Footer hideOnTouch />
                </VStack>
            </VStack>
        </VStack>
    );
};
